import { AlertTriangle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { hasAlreadyRecovered, isChunkLoadError, recoverFromChunkError } from '@/app/chunkRecovery';
import { BRAND } from '@/config/brand';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence.
 *
 * Wraps the providers as well as the routes, so a corrupt record in storage
 * cannot leave the user staring at a white page. Their data is untouched — the
 * recovery path is a reload, and only if that fails do we offer the destructive
 * option of clearing the device.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A stale chunk after a deployment is not an application fault. Reload once
    // to pick up the current entry HTML; if this session has already tried,
    // fall through to the error UI rather than looping.
    if (recoverFromChunkError(error)) return;

    // Kept to `console.error` on purpose: the product has no telemetry backend
    // and must not acquire one silently.
    console.error(`${BRAND.logTag} Unhandled error`, error, info.componentStack);
  }

  private readonly reload = () => window.location.reload();

  private readonly resetDevice = () => {
    try {
      const prefix = `${BRAND.storageNamespace}:`;
      const keys = Object.keys(window.localStorage).filter((key) => key.startsWith(prefix));
      for (const key of keys) window.localStorage.removeItem(key);
    } catch {
      // Nothing more we can do — fall through to the reload.
    }
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const isStaleChunk = isChunkLoadError(this.state.error);

    return (
      <div className="grid min-h-dvh place-items-center bg-canvas px-6">
        <div className="max-w-md text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-danger/10 text-danger">
            <AlertTriangle className="size-5" strokeWidth={2} />
          </span>
          <h1 className="mt-5 text-[20px] font-semibold tracking-[-0.02em] text-ink">
            {isStaleChunk ? `${BRAND.name} was updated` : `${BRAND.name} hit an unexpected error`}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-muted">
            {isStaleChunk
              ? 'A new version was released while this tab was open, and part of the old one is no longer available. Reloading loads the new version.'
              : 'Your business profile, catalog and quotes are still saved on this device. Reloading usually clears it.'}
          </p>

          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={this.reload}
              className="h-11 rounded-lg bg-accent px-5 text-[14px] font-medium text-accent-fg"
            >
              {isStaleChunk && hasAlreadyRecovered() ? 'Reload again' : `Reload ${BRAND.name}`}
            </button>
            <button
              type="button"
              onClick={this.resetDevice}
              className="text-[12.5px] text-subtle underline underline-offset-2"
            >
              Still broken? Clear this device's data
            </button>
          </div>

          <pre className="mt-6 max-h-32 overflow-auto rounded-lg bg-inset p-3 text-left text-[11px] leading-relaxed text-subtle">
            {this.state.error.message}
          </pre>
        </div>
      </div>
    );
  }
}
